import { el } from '../ui.js';
import {
  installState, promptInstall, onInstallChange, detectPlatform, type Platform,
} from '../install.js';

/**
 * The install page. Reached from the front page, and shown straight after
 * somebody creates an agency — the order matters: you sign up, then you put
 * the application on the devices you work from.
 *
 * Cellvora is distributed from its own website and nowhere else, so this page
 * is the whole distribution channel.
 */

/** Per-platform install steps, written for somebody who has never done this. */
const STEPS: Record<Platform, { label: string; icon: string; steps: string[] }> = {
  ios: {
    label: 'iPhone & iPad',
    icon: 'phone',
    steps: [
      'Open this page in Safari — installing does not work from Chrome or from a link inside another app on iOS.',
      'Tap the Share button at the bottom of the screen.',
      'Scroll down and tap "Add to Home Screen".',
      'Tap "Add". Cellvora now opens full screen from your home screen, like any other app.',
    ],
  },
  android: {
    label: 'Android',
    icon: 'phone',
    steps: [
      'Tap "Install Cellvora" above. If nothing happens, open the browser menu (⋮).',
      'Choose "Install app" or "Add to Home screen".',
      'Confirm. Cellvora installs like any other app and appears in your app drawer.',
    ],
  },
  desktop: {
    label: 'Windows, macOS & Linux',
    icon: 'laptop',
    steps: [
      'Click "Install Cellvora" above in Chrome, Edge, Brave or Arc.',
      'If you do not see the button, click the install icon in the address bar — a monitor with a downward arrow.',
      'Cellvora installs as a real desktop application, with its own window, its own dock or taskbar icon, and no browser tabs around it.',
      'On Safari for macOS, use File → Add to Dock instead.',
    ],
  },
  installed: {
    label: 'Already installed on this device',
    icon: 'check',
    steps: [
      'Open it from your app drawer, Applications folder, Start menu or home screen — it has the '
      + 'Cellvora icon and opens in its own window with no address bar.',
      'On a phone you can drag it onto a home screen. On a computer you can pin it to the dock or taskbar.',
      'If it is not in any of those places then it is not installed yet, whatever this page says. '
      + 'Follow the steps for your device below.',
    ],
  },
};

const ICONS: Record<string, string> = {
  phone: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"><rect x="6" y="2" width="12" height="20" rx="2.5"/><path d="M10.5 18.5h3"/></svg>',
  laptop: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="4" width="18" height="12" rx="2"/><path d="M2 20h20"/></svg>',
  check: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="m4 12.5 5.2 5.2L20 7"/></svg>',
  down: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M12 3v13"/><path d="m6.5 11.5 5.5 5.5 5.5-5.5"/><path d="M4 21h16"/></svg>',
};

function icon(name: string): HTMLElement {
  return el('span', { class: 'lp-ico', html: ICONS[name] ?? '' });
}

export interface InstallOptions {
  /** True when the person has just created an agency and is being walked on. */
  justSignedUp?: boolean;
  /** Where "carry on" goes. Absent means show a sign-in link instead. */
  onContinue?: () => void;
}

export function installView(go: (p: string) => void, opts: InstallOptions = {}): HTMLElement {
  const page = el('div', { class: 'lp' });
  const state = installState();
  const platform = state.platform;

  const installBtn = el('button', {
    class: 'lp-install', type: 'button', 'aria-live': 'polite',
  }, icon(platform === 'installed' ? 'check' : 'down'),
    el('span', { text: platform === 'installed' ? 'Cellvora is installed' : 'Install Cellvora' }));

  const hint = el('p', { class: 'lp-hint' });

  const paint = (): void => {
    const s = installState();
    installBtn.classList.toggle('is-done', s.platform === 'installed');
    // Disabled only when there is genuinely nothing the button could do: iOS
    // has no programmatic install at all. It is deliberately NOT disabled just
    // because we think this is the installed app — that guess can be wrong, and
    // a greyed-out button is no way to find out.
    installBtn.disabled = !s.canPrompt && s.platform === 'ios';
    hint.textContent = s.platform === 'installed'
      ? 'This looks like the installed app. If you are looking at a browser, the steps for your device are below.'
      : s.canPrompt
        ? 'One tap. No app store, no download to go and find.'
        : `Your browser installs this from its own menu — the steps for ${STEPS[s.platform].label} are below.`;
  };
  paint();
  onInstallChange(paint);
  installBtn.addEventListener('click', () => {
    void promptInstall().then((outcome) => {
      paint();
      if (outcome === 'accepted') {
        // "It worked" is not enough. An installed app lands in the app drawer
        // or the Applications folder, not on the screen in front of you, and
        // somebody still looking at the browser tab they started in reasonably
        // concludes that nothing happened.
        hint.textContent = detectPlatform() === 'android'
          ? 'Installed. It is in your app drawer — hold the icon to put it on a home screen.'
          : 'Installed. It is in your Applications folder or Start menu, and you can pin it to the dock or taskbar.';
      } else if (outcome === 'unavailable') {
        hint.textContent = `Your browser will not offer a one-tap install here. The steps for ${STEPS[detectPlatform()].label} are below.`;
      }
    });
  });

  /* --------------------------------------------------------------- head -- */
  page.append(
    el('section', { class: 'lp-enter' },
      opts.justSignedUp
        ? el('p', { class: 'lp-eyebrow', text: 'Your agency is created' })
        : null,
      el('h1', { class: 'lp-h1', text: opts.justSignedUp ? 'Now put it on your devices' : 'Install Cellvora' }),
      el('p', { class: 'lp-line' },
        opts.justSignedUp
          ? 'Install it on the phone and the computer you actually work from. The same account signs you in everywhere, and your book stays in step across all of them.'
          : 'Cellvora installs straight from this page. There is no app store listing and nothing to download and run — one tap and it is an application on your device.'),
      el('div', { class: 'lp-actions' }, installBtn),
      hint,
      opts.onContinue
        ? el('p', { class: 'lp-alt' },
          (() => {
            const a = el('button', { class: 'lnk', type: 'button', text: 'Skip for now and carry on to Cellvora →' });
            a.addEventListener('click', () => opts.onContinue!());
            return a;
          })())
        : el('p', { class: 'lp-alt' },
          'Already have an account? ',
          (() => {
            const a = el('button', { class: 'lnk', type: 'button', text: 'Sign in' });
            a.addEventListener('click', () => go('/sign-in'));
            return a;
          })())),
  );

  /* ---------------------------------------------------------------- how -- */
  const order: Platform[] = platform === 'installed'
    ? ['installed', 'ios', 'android', 'desktop']
    : [platform, ...(['ios', 'android', 'desktop'] as Platform[]).filter((p) => p !== platform)];

  page.append(
    el('section', { class: 'lp-how' },
      el('h2', { class: 'lp-h2', text: 'Every device your agency works from' }),
      el('p', { class: 'lp-sub' },
        'One account, every platform. Install it on a phone for the field and on a desktop for the office — '
        + 'it is the same application and the same data, and an update reaches all of them the moment it ships, '
        + 'with no app store review sitting in between.'),
      el('div', { class: 'lp-plats' },
        ...order.map((p, i) => el('article', { class: `lp-plat${i === 0 && p === platform ? ' is-you' : ''}` },
          el('div', { class: 'lp-plathead' },
            icon(STEPS[p].icon),
            el('h3', { text: STEPS[p].label }),
            ...(i === 0 && p === platform && p !== 'installed'
              ? [el('span', { class: 'lp-tag', text: 'This device' })] : [])),
          el('ol', { class: 'lp-steps' },
            ...STEPS[p].steps.map((t) => el('li', { text: t })))))),
      el('p', { class: 'lp-note' },
        el('b', null, 'Works offline. '),
        'The application opens and stays usable without a signal. Your leads are not cached to the device — '
        + 'a phone left in a taxi holds no customer records — so the parts that need the server say so plainly '
        + 'rather than showing you yesterday’s book as though it were today’s.')),
  );

  return page;
}
